import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Copy, Key, RefreshCw, ExternalLink, BookOpen, Check } from 'lucide-react';
import { apiKeysApi } from '../../../api/endpoints/apiKeys';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../components/ui/Alert';
import { formatDate } from '../../../lib/formatters';
import type { ApiKey } from '../../../types/apiKey';

interface Props { storeId: string; }

export function ApiKeysTab({ storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['*']);
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedBaseUrl, setCopiedBaseUrl] = useState(false);

  const baseUrl = `https://00pays.com/api/v1/dashboard/${storeId}`;

  const handleCopyBaseUrl = async () => {
    try {
      await navigator.clipboard.writeText(baseUrl);
      setCopiedBaseUrl(true);
      toast('success', t('settings.copiedToClipboard', 'Copied to clipboard'));
      setTimeout(() => setCopiedBaseUrl(false), 2000);
    } catch {
      toast('error', t('settings.copyFailed', 'Failed to copy'));
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys', storeId],
    queryFn: () => apiKeysApi.list(storeId),
  });

  const createMutation = useMutation({
    mutationFn: () => apiKeysApi.create(storeId, {
      name: newKeyName,
      permissions: newKeyPermissions.length > 0 ? newKeyPermissions : undefined,
      // Rate limits are enforced by the platform middleware
      // (code/routes/api/v1/middleware/rateLimiter.js) and aren't per-key
      // configurable - don't ask the merchant for a number we'd ignore.
      expiresAt: newKeyExpiry || undefined,
    }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', storeId] });
      setCreatedKey(result.apiKey);
      setNewKeyName('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const regenerateMutation = useMutation({
    mutationFn: (keyId: string) => apiKeysApi.regenerate(storeId, keyId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', storeId] });
      setCreatedKey(result.apiKey);
      setShowCreate(true);
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ keyId, isActive }: { keyId: string; isActive: boolean }) =>
      apiKeysApi.update(storeId, keyId, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const PERMISSION_SCOPES = [
    { value: '*', label: t('settings.fullAccess', 'Full Access') },
    { value: 'products', label: t('settings.products', 'Products') },
    { value: 'orders', label: t('settings.orders', 'Orders') },
    { value: 'customers', label: t('settings.customers', 'Customers') },
    { value: 'coupons', label: t('settings.coupons', 'Coupons') },
    { value: 'analytics.read', label: t('settings.analyticsRead', 'Analytics (read)') },
    { value: 'settings.read', label: t('settings.settingsRead', 'Settings (read)') },
    { value: 'shipping', label: t('settings.shipping', 'Shipping') },
  ];

  const togglePermission = (perm: string) => {
    if (perm === '*') {
      setNewKeyPermissions(newKeyPermissions.includes('*') ? [] : ['*']);
      return;
    }
    if (newKeyPermissions.includes('*')) {
      setNewKeyPermissions([perm]);
      return;
    }
    setNewKeyPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const resetCreateForm = () => {
    setShowCreate(false);
    setCreatedKey(null);
    setNewKeyName('');
    setNewKeyPermissions(['*']);
    setNewKeyExpiry('');
  };

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => apiKeysApi.delete(storeId, keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const columns: Column<ApiKey>[] = [
    {
      key: 'name',
      header: t('common.name'),
      render: (k) => (
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-text-muted" />
          <span className="font-medium">{k.name}</span>
        </div>
      ),
    },
    {
      key: 'prefix',
      header: t('settings.keyPrefix'),
      render: (k) => <span className="font-mono text-xs text-text-muted">{k.keyPrefix}...</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (k) => <Badge variant={k.isActive ? 'success' : 'default'}>{k.isActive ? t('coupons.active') : t('coupons.inactive')}</Badge>,
    },
    {
      key: 'lastUsed',
      header: t('settings.lastUsed'),
      render: (k) => <span className="text-xs text-text-secondary">{k.lastUsed ? formatDate(k.lastUsed) : '-'}</span>,
    },
    {
      key: 'created',
      header: t('common.date'),
      render: (k) => <span className="text-xs text-text-secondary">{formatDate(k.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (k) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => toggleMutation.mutate({ keyId: k._id, isActive: !k.isActive })}
            className={`rounded-lg p-1.5 text-xs font-medium transition-colors ${k.isActive ? 'text-success hover:text-warning' : 'text-text-muted hover:text-success'}`}
            title={k.isActive ? t('common.disable', 'Disable') : t('common.enable', 'Enable')}
          >
            {k.isActive ? '●' : '○'}
          </button>
          <button
            onClick={() => regenerateMutation.mutate(k._id)}
            className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
            title={t('settings.regenerate', 'Regenerate')}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => deleteMutation.mutate(k._id)}
            className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
            title={t('common.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const apiKeys = data?.apiKeys ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* API Documentation Banner */}
      <section className="rounded-xl border border-info/25 bg-info/10 p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-info/20 p-2.5 shrink-0">
            <BookOpen className="h-5 w-5 text-info" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-text-primary">
              {t('settings.apiDocumentation', 'API Documentation')}
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              {t('settings.apiDocumentationDesc', 'Explore the interactive API documentation to learn how to integrate with your store')}
            </p>
            <a
              href="/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-info/20 px-4 py-2 text-sm font-medium text-info hover:bg-info/30 transition-colors"
            >
              {t('settings.viewDocumentation', 'View Documentation')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* API Base URL */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-3">
        <h2 className="text-base font-semibold">{t('settings.apiBaseUrl', 'API Base URL')}</h2>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-bg-field px-3 py-2">
          <div className="min-w-0">
            <span className="text-xs text-text-muted">{t('settings.baseUrl', 'Base URL')}</span>
            <p className="font-mono text-sm text-text-primary truncate">{baseUrl}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyBaseUrl}
            className="shrink-0"
          >
            {copiedBaseUrl ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            {copiedBaseUrl
              ? t('settings.copied', 'Copied')
              : t('settings.copy', 'Copy')}
          </Button>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          {t('settings.createApiKey')}
        </Button>
      </div>
      <div className="rounded-xl border border-border-default bg-bg-secondary">
        <DataTable columns={columns} data={apiKeys} isLoading={isLoading} keyExtractor={(k) => k._id} emptyTitle={t('common.noData')} />
      </div>

      <Modal
        isOpen={showCreate}
        onClose={resetCreateForm}
        title={createdKey ? t('settings.apiKeyCreated') : t('settings.createApiKey')}
        size="lg"
        footer={
          createdKey ? (
            <Button onClick={resetCreateForm}>{t('common.close')}</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={resetCreateForm}>{t('common.cancel')}</Button>
              <Button onClick={() => createMutation.mutate()} isLoading={createMutation.isPending} disabled={!newKeyName.trim()}>
                {t('common.create')}
              </Button>
            </>
          )
        }
      >
        {createdKey ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">{t('settings.saveKeyWarning')}</p>
            <div className="flex items-center gap-2 rounded-lg bg-bg-tertiary p-3">
              <code className="flex-1 text-sm break-all">{createdKey}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(createdKey); toast('success', t('paylinks.linkCopied')); }}
                className="rounded-lg p-1.5 text-text-muted hover:text-accent"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <Input label={t('common.name')} required value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />

            {/* Permissions grid */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">{t('settings.permissions', 'Permissions')}</label>
              <div className="grid grid-cols-2 gap-2">
                {PERMISSION_SCOPES.map((scope) => (
                  <button
                    key={scope.value}
                    type="button"
                    onClick={() => togglePermission(scope.value)}
                    className={`rounded-lg border px-3 py-2 text-xs font-medium text-start transition-colors ${
                      newKeyPermissions.includes(scope.value) || (newKeyPermissions.includes('*') && scope.value !== '*')
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border-default text-text-secondary hover:border-accent/50'
                    }`}
                  >
                    {scope.label}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label={t('settings.expiryDate', 'Expiry Date')}
              type="date"
              value={newKeyExpiry}
              onChange={(e) => setNewKeyExpiry(e.target.value)}
            />
            <div className="rounded-lg border border-border-default bg-bg-tertiary px-3 py-2.5 text-xs text-text-muted leading-relaxed">
              {t(
                'settings.rateLimitInfo',
                'Rate limits are set by the platform: 100 req/min for dashboard endpoints, 60 req/min for paylink endpoints. Contact support if you need more.',
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
